import { useRef, useState, type ReactNode } from 'react';
import {
  cn,
  Icon,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@tutero/ui';

/**
 * What a sentence of explanation becomes anywhere in the admin: an ⓘ beside the thing it explains,
 * always shown, the sentence in its tooltip — the hover is the icon's alone, never a value's or a
 * row's. Drawn as MathGaps/schools draws its own (`features/billing/ResetInfo`,
 * Sonny, 2026-10-01): no hover delay, the ⓘ fills blue while it is open, and the tooltip sits above
 * it, narrow and centred. A heading's opens to its right instead (`side="right"`): a title at the
 * top of a page or a dialog has no room above, and below it the tooltip would cover the controls.
 *
 * The ⓘ does nothing but say this, so a press opens it too: a finger never hovers, and Radix's
 * tooltip opens only to a hover or a keyboard's focus and shuts on a press. A tap opens it and a
 * second tap, or one anywhere else, shuts it, as Enter or Space does; a mouse's press keeps open
 * what its hover opened. `label` is what a screen reader hears the ⓘ called; `className` sets
 * the ⓘ's ink at rest where the muted grey would not show (white on a coloured block).
 */
export function InfoTip({
  label = 'More information',
  side = 'top',
  className,
  children,
}: {
  label?: string;
  side?: 'top' | 'right';
  className?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  /* Read at the press's start, before a tap outside the tooltip shuts it. */
  const press = useRef({ wasOpen: false, mouse: false });

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={label}
            /* `preventDefault` skips the trigger's own shut-on-press. */
            onPointerDown={(event) => {
              press.current = {
                wasOpen: open,
                mouse: event.pointerType === 'mouse',
              };
              event.preventDefault();
            }}
            onClick={(event) => {
              event.preventDefault();
              /* A key's press (Enter, Space) has no pointer: it toggles, as a tap does. */
              if (event.detail === 0) setOpen(!open);
              else setOpen(press.current.mouse || !press.current.wasOpen);
            }}
            className={cn(
              'focus-visible:ring-ring inline-grid size-5 shrink-0 cursor-pointer place-items-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
              open ? 'text-blue-500' : (className ?? 'text-muted-foreground'),
            )}
          >
            <Icon name="info" size={16} filled={open} />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side={side}
          sideOffset={side === 'top' ? 2 : 6}
          /* Beside a heading there is width to spare and little height before the controls under
             it, so the tooltip widens to stay a few lines short. */
          className={cn(
            'text-center text-balance',
            side === 'top' ? 'max-w-[220px]' : 'max-w-[340px]',
          )}
        >
          {children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
